'use client';

import { Session } from 'next-auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle, Users, Target, Zap, TrendingUp, DollarSign, Clock, Shield, Building2, Rocket } from 'lucide-react';
import CalendarLink from '@/components/ui/calendar-link';

interface AgenciesClientProps {
    initialSession: Session | null;
}

export default function AgenciesClient({ initialSession }: AgenciesClientProps) {

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white">
            {/* Hero Section */}
            <section className="relative px-6 pt-20 pb-16 lg:px-8">
                <div className="mx-auto max-w-6xl text-center">
                    <Badge className="mb-6 bg-green-100 text-green-700 hover:bg-green-200">
                        Join 100+ Other Leading Agencies
                    </Badge>

                    <h1 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl md:text-6xl lg:text-7xl">
                        Build Your AI Sales Force.
                        <br />
                        <span className="bg-gradient-to-r from-teal-500 to-green-500 bg-clip-text text-transparent">Scale Revenue Infinitely.</span>
                    </h1>

                    <p className="mt-6 text-lg sm:text-xl leading-7 sm:leading-8 text-gray-600 max-w-3xl mx-auto px-4 sm:px-0">
                        Stop trading hours for dollars. With NOS Agents, transform your GTM expertise
                        into proprietary AI agents that work for you 24/7, unlocking unprecedented scale and passive income.
                    </p>

                    <div className="mt-10 flex items-center justify-center">
                        <CalendarLink
                            href="https://cal.com/up-punyagupta/chat-with-founders"
                            target="_blank"
                            className="inline-flex items-center justify-center w-full sm:w-auto bg-gradient-to-r from-teal-500 to-green-500 hover:from-teal-600 hover:to-green-600 text-white px-8 py-4 border-0 rounded-lg text-lg font-semibold shadow-lg"
                        >
                            Let's Talk
                            <ArrowRight className="ml-2 h-5 w-5" />
                        </CalendarLink>
                    </div>
                </div>
            </section>

            {/* Problem Section */}
            <section className="px-6 py-24 lg:px-8 bg-white">
                <div className="mx-auto max-w-6xl">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                            Is Your Agency Hitting a Wall?
                        </h2>
                        <p className="mt-4 text-lg text-gray-600">
                            Manual outreach, client churn, and the endless sales grind can stifle even the best GTM teams. Sound familiar?
                        </p>
                    </div>

                    <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
                        <Card className="relative overflow-hidden border-0 shadow-lg">
                            <CardContent className="p-8">
                                <div className="mb-6">
                                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-red-100">
                                        <TrendingUp className="h-6 w-6 text-red-600" />
                                    </div>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                                    Client Acquisition Treadmill
                                </h3>
                                <p className="text-gray-600">
                                    Spending too much time finding and onboarding new clients, not enough on
                                    delivering results?
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="relative overflow-hidden border-0 shadow-lg">
                            <CardContent className="p-8">
                                <div className="mb-6">
                                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-red-100">
                                        <Clock className="h-6 w-6 text-red-600" />
                                    </div>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                                    Manual Outreach Limits
                                </h3>
                                <p className="text-gray-600">
                                    Are your team's hours the bottleneck to scaling your client impact and revenue?
                                </p>
                            </CardContent>
                        </Card>

                        <Card className="relative overflow-hidden border-0 shadow-lg">
                            <CardContent className="p-8">
                                <div className="mb-6">
                                    <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-red-100">
                                        <Target className="h-6 w-6 text-red-600" />
                                    </div>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                                    Unpredictable Client Success
                                </h3>
                                <p className="text-gray-600">
                                    Struggling to know which clients will truly benefit from your unique methods
                                    before investing heavily?
                                </p>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </section>

            {/* How It Works Section */}
            <section className="px-6 py-24 lg:px-8 bg-gray-50">
                <div className="mx-auto max-w-6xl">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                            How It Works
                        </h2>
                        <p className="mt-4 text-lg text-gray-600">
                            Transform your expertise into automated revenue engines with our proven 3-step process
                        </p>
                    </div>

                    {/* Step 1 */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
                        <div className="lg:order-1">
                            <div className="flex items-center mb-6">
                                <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-teal-500 to-green-500 text-white text-2xl font-bold mr-4">
                                    1
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900">
                                    Select Your Ideal Client Profiles
                                </h3>
                            </div>
                            <p className="text-lg text-gray-600 mb-6">
                                Browse from thousands of qualified client segments and their detailed groupings. Find the perfect match for your expertise.
                            </p>
                            <div className="space-y-4">
                                <div className="flex items-start space-x-3">
                                    <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <div className="font-semibold text-gray-900">Target High-Value Segments</div>
                                        <div className="text-gray-600">Digital Ad Agencies seeking Legal Clients with $50k+ monthly budgets</div>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <div className="font-semibold text-gray-900">High-Bid Opportunities</div>
                                        <div className="text-gray-600">SaaS Companies with proven PMF looking for enterprise sales expertise</div>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <div className="font-semibold text-gray-900">Niche Specializations</div>
                                        <div className="text-gray-600">E-commerce brands needing influencer outreach for luxury goods</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="lg:order-2">
                            {/* Mock UI Screenshot */}
                            <div className="bg-gray-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-700">
                                {/* Mock Browser Header */}
                                <div className="bg-gray-800 px-4 py-3 flex items-center space-x-2">
                                    <div className="flex space-x-2">
                                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                    </div>
                                    <div className="flex-1 ml-4">
                                        <div className="bg-gray-700 rounded-md px-3 py-1 text-xs text-gray-300 font-mono">
                                            nos-agents.com/marketplace
                                        </div>
                                    </div>
                                </div>

                                {/* Mock UI Content */}
                                <div className="bg-white p-6">
                                    {/* Header */}
                                    <div className="flex items-center justify-between mb-6">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900">Client Marketplace</h3>
                                            <p className="text-sm text-gray-600">1,247 active opportunities</p>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <div className="bg-gray-100 rounded-lg px-3 py-1 text-xs font-medium text-gray-700">
                                                Sort by: Highest Pay
                                            </div>
                                        </div>
                                    </div>

                                    {/* Client Cards */}
                                    <div className="space-y-4">
                                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                                        <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                                                            <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <h4 className="font-semibold text-gray-900">Digital Ad Agencies</h4>
                                                        <p className="text-sm text-gray-600">Target: Legal Clients</p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium mb-1">
                                                        $200/booking
                                                    </div>
                                                    <p className="text-xs text-gray-500">47 customers</p>
                                                </div>
                                            </div>
                                            <div className="flex justify-end">
                                                <button className="bg-teal-600 text-white text-xs px-3 py-1 rounded-md">
                                                    View Details
                                                </button>
                                            </div>
                                        </div>

                                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                                        <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                            <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <h4 className="font-semibold text-gray-900">SaaS Startups</h4>
                                                        <p className="text-sm text-gray-600">Target: Enterprise Sales</p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded-full font-medium mb-1">
                                                        $100/booking
                                                    </div>
                                                    <p className="text-xs text-gray-500">128 customers</p>
                                                </div>
                                            </div>
                                            <div className="flex justify-end">
                                                <button className="bg-teal-600 text-white text-xs px-3 py-1 rounded-md">
                                                    View Details
                                                </button>
                                            </div>
                                        </div>

                                        <div className="bg-white border border-gray-200 rounded-lg p-4">
                                            <div className="flex items-start justify-between mb-3">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                                        <svg className="w-5 h-5 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M10 2L3 7v11a1 1 0 001 1h12a1 1 0 001-1V7l-7-5zM6 9.5a.5.5 0 01.5-.5h7a.5.5 0 010 1h-7a.5.5 0 01-.5-.5zm.5 2.5a.5.5 0 000 1h7a.5.5 0 000-1h-7z" clipRule="evenodd" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <h4 className="font-semibold text-gray-900">E-commerce Brands</h4>
                                                        <p className="text-sm text-gray-600">Target: Luxury Influencers</p>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium mb-1">
                                                        $175/booking
                                                    </div>
                                                    <p className="text-xs text-gray-500">23 customers</p>
                                                </div>
                                            </div>
                                            <div className="flex justify-end">
                                                <button className="bg-teal-600 text-white text-xs px-3 py-1 rounded-md">
                                                    View Details
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Bottom pagination/stats */}
                                    <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
                                        <p className="text-xs text-gray-500">Showing 3 of 1,247 opportunities</p>
                                        <div className="flex items-center space-x-2">
                                            <button className="text-xs text-gray-400">Previous</button>
                                            <div className="flex space-x-1">
                                                <div className="w-6 h-6 bg-teal-600 text-white text-xs rounded flex items-center justify-center">1</div>
                                                <div className="w-6 h-6 bg-gray-100 text-gray-600 text-xs rounded flex items-center justify-center">2</div>
                                                <div className="w-6 h-6 bg-gray-100 text-gray-600 text-xs rounded flex items-center justify-center">3</div>
                                            </div>
                                            <button className="text-xs text-teal-600">Next</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Step 2 */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
                        <div className="lg:order-2">
                            <div className="flex items-center mb-6">
                                <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-purple-500 text-white text-2xl font-bold mr-4">
                                    2
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900">
                                    Build Your Workflow Agent
                                </h3>
                            </div>
                            <p className="text-lg text-gray-600 mb-6">
                                Train your workflow agent conversationally or import from existing tools. Your data stays yours, strategy remains protected, all powered by NOS infrastructure.
                            </p>
                            <div className="space-y-4">
                                <div className="flex items-start space-x-3">
                                    <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <div className="font-semibold text-gray-900">Conversational Training</div>
                                        <div className="text-gray-600">Train your agent through natural conversation about your proven methodologies</div>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <div className="font-semibold text-gray-900">Import Existing Workflows</div>
                                        <div className="text-gray-600">Seamlessly import from N8N, Clay, or upload your static datasources</div>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <div className="font-semibold text-gray-900">Protected & Powered</div>
                                        <div className="text-gray-600">Strategy obfuscated from clients, emails sent via <a href="https://nos.email" className="text-teal-600 underline">NOS.Email</a></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="lg:order-1">
                            {/* Mock Workflow Builder Screenshot */}
                            <div className="bg-gray-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-700">
                                {/* Mock Browser Header */}
                                <div className="bg-gray-800 px-4 py-3 flex items-center space-x-2">
                                    <div className="flex space-x-2">
                                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                    </div>
                                    <div className="flex-1 ml-4">
                                        <div className="bg-gray-700 rounded-md px-3 py-1 text-xs text-gray-300 font-mono">
                                            nos-agents.com/workflow-builder
                                        </div>
                                    </div>
                                </div>

                                {/* Mock Workflow Builder Content */}
                                <div className="bg-white p-6">
                                    {/* Header */}
                                    <div className="flex items-center justify-between mb-6">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900">Workflow Builder</h3>
                                            <p className="text-sm text-gray-600">Legal Client Outreach Agent</p>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <div className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-medium">
                                                Training
                                            </div>
                                        </div>
                                    </div>

                                    {/* Conversational Training Interface */}
                                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                                        <div className="text-sm font-medium text-gray-900 mb-3">Training Your Agent</div>
                                        <div className="space-y-4">
                                            <div className="bg-blue-50 border-l-4 border-blue-500 p-3">
                                                <div className="text-xs text-blue-700 mb-1">Your Instructions:</div>
                                                <div className="text-sm text-gray-800">"Navigate to HackerNews ShowHN and try to find emails from legal tech startups. If you can't find emails, navigate to their GitHub page and look for contact information in their README or profile."</div>
                                            </div>

                                            <div className="bg-green-50 border-l-4 border-green-500 p-3">
                                                <div className="text-xs text-green-700 mb-1">Agent Understanding:</div>
                                                <div className="text-sm text-gray-800">✓ Search HackerNews ShowHN for legal tech posts<br />✓ Extract contact emails from posts<br />✓ Fallback to GitHub if no email found<br />✓ Check README and profile for contact info</div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Generated Workflow Steps */}
                                    <div className="space-y-3">
                                        <div className="text-sm font-medium text-gray-900">Generated Workflow:</div>

                                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center text-white text-xs font-bold">1</div>
                                                <h4 className="font-medium text-gray-900 text-sm">Scrape HackerNews ShowHN</h4>
                                            </div>
                                            <p className="text-xs text-gray-600 ml-9">Search for legal tech, lawyer, law firm related posts</p>
                                        </div>

                                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center text-white text-xs font-bold">2</div>
                                                <h4 className="font-medium text-gray-900 text-sm">Extract Contact Info</h4>
                                            </div>
                                            <p className="text-xs text-gray-600 ml-9">Parse posts for email addresses and website links</p>
                                        </div>

                                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <div className="w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center text-white text-xs font-bold">3</div>
                                                <h4 className="font-medium text-gray-900 text-sm">GitHub Fallback</h4>
                                            </div>
                                            <p className="text-xs text-gray-600 ml-9">If no email found, check GitHub profile and README files</p>
                                        </div>

                                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                                            <div className="flex items-center space-x-3 mb-2">
                                                <div className="w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs font-bold">4</div>
                                                <h4 className="font-medium text-gray-900 text-sm">Validate & Store</h4>
                                            </div>
                                            <p className="text-xs text-gray-600 ml-9">Verify email format and store in CRM with source tags</p>
                                        </div>
                                    </div>

                                    {/* Training Input */}
                                    <div className="mt-6 pt-4 border-t border-gray-200">
                                        <div className="flex items-center justify-between mb-3">
                                            <div className="text-xs text-gray-500">Continue Training</div>
                                            <button className="text-xs bg-blue-600 text-white px-3 py-1 rounded">Add Step</button>
                                        </div>
                                        <div className="bg-white border border-gray-300 rounded-lg p-3">
                                            <div className="text-xs text-gray-400 mb-1">Tell your agent what to do next...</div>
                                            <div className="text-sm text-gray-600">Type: "Also check their LinkedIn company page for additional contacts..."</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Step 3 */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
                        <div className="lg:order-1">
                            <div className="flex items-center mb-6">
                                <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-pink-500 text-white text-2xl font-bold mr-4">
                                    3
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900">
                                    Train Your Reply Agent
                                </h3>
                            </div>
                            <p className="text-lg text-gray-600 mb-6">
                                Conversationally train your reply agent to handle responses professionally or use our proven default agent. All credits covered by NOS.
                            </p>
                            <div className="space-y-4">
                                <div className="flex items-start space-x-3">
                                    <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <div className="font-semibold text-gray-900">Natural Training</div>
                                        <div className="text-gray-600">Teach your agent how to respond to common objections and questions</div>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <div className="font-semibold text-gray-900">Default Agent Available</div>
                                        <div className="text-gray-600">Use our proven default reply agent trained on thousands of successful conversations</div>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <div className="font-semibold text-gray-900">Zero Additional Costs</div>
                                        <div className="text-gray-600">All AI credits and infrastructure costs covered by NOS platform</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="lg:order-2">
                            {/* Mock Reply Agent Screenshot */}
                            <div className="bg-gray-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-700">
                                {/* Mock Browser Header */}
                                <div className="bg-gray-800 px-4 py-3 flex items-center space-x-2">
                                    <div className="flex space-x-2">
                                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                    </div>
                                    <div className="flex-1 ml-4">
                                        <div className="bg-gray-700 rounded-md px-3 py-1 text-xs text-gray-300 font-mono">
                                            nos-agents.com/reply-agent
                                        </div>
                                    </div>
                                </div>

                                {/* Mock Reply Agent Content */}
                                <div className="bg-white p-6">
                                    {/* Header */}
                                    <div className="flex items-center justify-between mb-6">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900">Reply Agent Training</h3>
                                            <p className="text-sm text-gray-600">Legal Services Agent</p>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <div className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">
                                                8/12 Complete
                                            </div>
                                        </div>
                                    </div>

                                    {/* Active Training Session */}
                                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                                        <div className="text-sm font-medium text-gray-900 mb-3">Current Training Session</div>

                                        <div className="space-y-4">
                                            <div className="bg-red-50 border-l-4 border-red-400 p-3">
                                                <div className="text-xs text-red-700 mb-1">Prospect Response:</div>
                                                <div className="text-sm text-gray-800">"We already have a marketing agency and we're happy with them. Not interested."</div>
                                            </div>

                                            <div className="bg-white border border-gray-300 rounded-lg p-3">
                                                <div className="text-xs text-gray-500 mb-2">Train your agent's response:</div>
                                                <div className="bg-blue-50 p-3 rounded text-sm text-gray-800 mb-3">
                                                    "I completely understand - having a good agency relationship is valuable. I'm not looking to replace anyone. I specialize specifically in legal client acquisition, which is quite different from general marketing. Our recent client saw a 400% increase in qualified legal leads using our specialized approach. Would you be open to a brief 10-minute call to share what's working? No pitch, just industry insights."
                                                </div>
                                                <div className="flex space-x-2">
                                                    <button className="text-xs bg-green-600 text-white px-3 py-1 rounded">✓ Good Response</button>
                                                    <button className="text-xs bg-yellow-600 text-white px-3 py-1 rounded">↻ Revise</button>
                                                    <button className="text-xs bg-red-600 text-white px-3 py-1 rounded">✗ Poor Response</button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Training Examples */}
                                    <div className="space-y-3">
                                        <div className="text-sm font-medium text-gray-900">Recent Training Examples:</div>

                                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                                            <div className="flex justify-between items-start mb-2">
                                                <div className="text-xs font-medium text-gray-900">Price Objection</div>
                                                <div className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Trained ✓</div>
                                            </div>
                                            <div className="text-xs text-gray-600">"Your pricing seems high..." → Focus on ROI and case studies</div>
                                        </div>

                                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                                            <div className="flex justify-between items-start mb-2">
                                                <div className="text-xs font-medium text-gray-900">Timing Concern</div>
                                                <div className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">Trained ✓</div>
                                            </div>
                                            <div className="text-xs text-gray-600">"Not ready right now..." → Offer value-first content</div>
                                        </div>

                                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                                            <div className="flex justify-between items-start mb-2">
                                                <div className="text-xs font-medium text-gray-900">Authority Challenge</div>
                                                <div className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">In Progress</div>
                                            </div>
                                            <div className="text-xs text-gray-600">"I need to check with my partner..." → Currently training response</div>
                                        </div>
                                    </div>

                                    {/* Quick Training Input */}
                                    <div className="mt-6 pt-4 border-t border-gray-200">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="text-xs text-gray-500">Quick Training</div>
                                            <button className="text-xs bg-blue-600 text-white px-3 py-1 rounded">Add Scenario</button>
                                        </div>
                                        <div className="grid grid-cols-2 gap-3">
                                            <div className="bg-gray-100 rounded p-2 text-center">
                                                <div className="text-xs text-gray-600">Budget Concerns</div>
                                            </div>
                                            <div className="bg-gray-100 rounded p-2 text-center">
                                                <div className="text-xs text-gray-600">Competitor Mention</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Step 4 */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div className="lg:order-2">
                            <div className="flex items-center mb-6">
                                <div className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-teal-500 text-white text-2xl font-bold mr-4">
                                    4
                                </div>
                                <h3 className="text-2xl font-bold text-gray-900">
                                    Get Paid Per Booking
                                </h3>
                            </div>
                            <p className="text-lg text-gray-600 mb-6">
                                Earn revenue when your agents successfully book qualified meetings. We handle all payments and fulfillment, so you can focus on what you do best.
                            </p>
                            <div className="space-y-4">
                                <div className="flex items-start space-x-3">
                                    <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <div className="font-semibold text-gray-900">Performance-Based Earnings</div>
                                        <div className="text-gray-600">Get paid only for successful bookings - aligning our incentives with your success</div>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <div className="font-semibold text-gray-900">Direct Payments</div>
                                        <div className="text-gray-600">Payments come directly from NOS - no chasing clients for payment</div>
                                    </div>
                                </div>
                                <div className="flex items-start space-x-3">
                                    <CheckCircle className="h-6 w-6 text-green-500 mt-0.5 flex-shrink-0" />
                                    <div>
                                        <div className="font-semibold text-gray-900">Fulfillment Protection</div>
                                        <div className="text-gray-600">We handle any fulfillment issues or disputes - your reputation stays protected</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="lg:order-1">
                            {/* Mock Payment Dashboard Screenshot */}
                            <div className="bg-gray-900 rounded-2xl shadow-2xl overflow-hidden border border-gray-700">
                                {/* Mock Browser Header */}
                                <div className="bg-gray-800 px-4 py-3 flex items-center space-x-2">
                                    <div className="flex space-x-2">
                                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                        <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                    </div>
                                    <div className="flex-1 ml-4">
                                        <div className="bg-gray-700 rounded-md px-3 py-1 text-xs text-gray-300 font-mono">
                                            nos-agents.com/earnings
                                        </div>
                                    </div>
                                </div>

                                {/* Mock Earnings Dashboard Content */}
                                <div className="bg-white p-6">
                                    {/* Header */}
                                    <div className="flex items-center justify-between mb-6">
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900">Earnings Dashboard</h3>
                                            <p className="text-sm text-gray-600">This Month: December 2024</p>
                                        </div>
                                        <div className="flex items-center space-x-2">
                                            <div className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">
                                                $12,400 Earned
                                            </div>
                                        </div>
                                    </div>

                                    {/* Earnings Cards */}
                                    <div className="grid grid-cols-2 gap-4 mb-6">
                                        <div className="bg-blue-50 rounded-lg p-4">
                                            <div className="text-2xl font-bold text-blue-600 mb-1">47</div>
                                            <div className="text-sm text-blue-700">Bookings Made</div>
                                        </div>
                                        <div className="bg-green-50 rounded-lg p-4">
                                            <div className="text-2xl font-bold text-green-600 mb-1">$264</div>
                                            <div className="text-sm text-green-700">Avg Per Booking</div>
                                        </div>
                                    </div>

                                    {/* Recent Bookings */}
                                    <div className="space-y-3">
                                        <div className="text-sm font-medium text-gray-900">Recent Bookings</div>

                                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                                            <div className="flex justify-between items-center">
                                                <div>
                                                    <div className="font-medium text-gray-900 text-sm">Miller & Associates Law</div>
                                                    <div className="text-xs text-gray-600">Digital Ad Campaign • Dec 15</div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="font-bold text-green-600 text-sm">$200</div>
                                                    <div className="text-xs text-gray-500">Paid</div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                                            <div className="flex justify-between items-center">
                                                <div>
                                                    <div className="font-medium text-gray-900 text-sm">TechFlow SaaS</div>
                                                    <div className="text-xs text-gray-600">Enterprise Sales • Dec 14</div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="font-bold text-green-600 text-sm">$175</div>
                                                    <div className="text-xs text-gray-500">Paid</div>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-white border border-gray-200 rounded-lg p-3">
                                            <div className="flex justify-between items-center">
                                                <div>
                                                    <div className="font-medium text-gray-900 text-sm">Luxe Beauty Co.</div>
                                                    <div className="text-xs text-gray-600">Influencer Outreach • Dec 13</div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="font-bold text-blue-600 text-sm">$185</div>
                                                    <div className="text-xs text-blue-500">Processing</div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Payout Info */}
                                    <div className="mt-6 pt-4 border-t border-gray-200">
                                        <div className="flex items-center justify-between">
                                            <p className="text-xs text-gray-500">Next Payout: Dec 31, 2024</p>
                                            <button className="text-xs bg-teal-600 text-white px-3 py-1 rounded">
                                                View Details
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>



            {/* Benefits Section */}
            <section className="px-6 py-24 lg:px-8 bg-gray-50">
                <div className="mx-auto max-w-6xl">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                            The Unfair Advantage for Your Agency
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                        <div className="space-y-8">
                            <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0">
                                    <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-teal-100">
                                        <Building2 className="h-5 w-5 text-teal-600" />
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        Build Proprietary AI Assets
                                    </h3>
                                    <p className="text-gray-600">
                                        Your expertise, codified into AI agents you own. A lasting asset for your agency.
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0">
                                    <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                                        <TrendingUp className="h-5 w-5 text-green-600" />
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        Achieve True Scalability
                                    </h3>
                                    <p className="text-gray-600">
                                        Break free from manual limits. Serve more clients with less overhead.
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0">
                                    <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-teal-100">
                                        <Clock className="h-5 w-5 text-teal-600" />
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        Generate Passive Revenue
                                    </h3>
                                    <p className="text-gray-600">
                                        Your AI agents work 24/7, booking meetings and earning for you around the clock.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-8">
                            <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0">
                                    <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                                        <Target className="h-5 w-5 text-green-600" />
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        Reduce Client Acquisition Costs
                                    </h3>
                                    <p className="text-gray-600">
                                        Focus on building great agents; we help connect you to high-quality client campaigns.
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0">
                                    <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-teal-100">
                                        <DollarSign className="h-5 w-5 text-teal-600" />
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        Slash Operational Overheads
                                    </h3>
                                    <p className="text-gray-600">
                                        Automate outreach tasks and potentially share dataset costs with the NOS Agents community.
                                    </p>
                                </div>
                            </div>

                            <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0">
                                    <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-green-100">
                                        <Rocket className="h-5 w-5 text-green-600" />
                                    </div>
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                        Future-Proof Your Agency
                                    </h3>
                                    <p className="text-gray-600">
                                        Lead the charge in AI-driven GTM. Offer services your competitors can't match.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="px-6 py-24 lg:px-8 bg-white">
                <div className="mx-auto max-w-4xl">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
                            Start Building Your AI Agency Today
                        </h2>
                        <p className="mt-4 text-lg text-gray-600">
                            Book a strategy call to explore how NOS Agents can help you build
                            proprietary AI, automate revenue, and scale your impact like never before.
                        </p>
                    </div>

                    <div className="text-center">
                        <CalendarLink
                            href="https://cal.com/up-punyagupta/chat-with-founders"
                            target="_blank"
                            className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold rounded-lg bg-gradient-to-r from-teal-500 to-green-500 text-white shadow-lg"
                        >
                            Let's Talk
                            <ArrowRight className="ml-2 h-5 w-5" />
                        </CalendarLink>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-white border-t">
                <div className="mx-auto max-w-6xl px-6 py-12 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        <div className="md:col-span-2">
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-green-500 rounded-xl flex items-center justify-center shadow-md">
                                    <span className="text-white font-bold text-lg tracking-wider">NOS</span>
                                </div>
                                <span className="text-xl font-bold text-gray-900">Agents</span>
                            </div>
                            <p className="text-gray-600 max-w-md">
                                Build your AI Sales Force. Scale your agency infinitely.
                            </p>
                        </div>

                        <div>
                            <h3 className="text-sm font-semibold text-gray-900 mb-4">Platform</h3>
                            <ul className="space-y-2 text-sm text-gray-600">
                                <li><a href="/" className="hover:text-teal-600">For Clients</a></li>
                                <li><a href="/agencies" className="hover:text-teal-600">For Agencies</a></li>
                                <li><a href="#" className="hover:text-teal-600">Pricing</a></li>
                            </ul>
                        </div>

                        <div>
                            <h3 className="text-sm font-semibold text-gray-900 mb-4">Company</h3>
                            <ul className="space-y-2 text-sm text-gray-600">
                                <li><a href="#" className="hover:text-teal-600">About Us</a></li>
                                <li><a href="#" className="hover:text-teal-600">Contact</a></li>
                                <li><a href="/privacy" className="hover:text-teal-600">Privacy Policy</a></li>
                                <li><a href="/terms" className="hover:text-teal-600">Terms of Service</a></li>
                            </ul>
                        </div>
                    </div>

                    <div className="border-t mt-8 pt-8 text-center">
                        <p className="text-sm text-gray-600">
                            © 2025 NOS Agents. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
